import { useMemo } from 'react';
import { PaletteRecord, PaletteHistoryRecord } from '../lib/database';
import { colorEngine } from '../lib/colorEngine';
import { XCircle, ArrowRight, AlertTriangle, CheckCircle, Info } from 'lucide-react';

interface PaletteComparisonModalProps {
  currentPalette: PaletteRecord;
  comparisonVersion: PaletteHistoryRecord;
  onClose: () => void;
}

interface ColorChange {
  index: number;
  oldColor?: string;
  newColor?: string;
  deltaE?: number;
  changeType: 'added' | 'removed' | 'modified' | 'unchanged';
}

export function PaletteComparisonModal({ currentPalette, comparisonVersion, onClose }: PaletteComparisonModalProps) {
  const changes = useMemo(() => {
    const oldColors = comparisonVersion.colors;
    const newColors = currentPalette.colors;
    const maxLength = Math.max(oldColors.length, newColors.length);
    const changeList: ColorChange[] = [];

    for (let i = 0; i < maxLength; i++) {
      const oldColor = oldColors[i];
      const newColor = newColors[i];

      if (!oldColor && newColor) {
        changeList.push({
          index: i,
          newColor,
          changeType: 'added',
        });
      } else if (oldColor && !newColor) {
        changeList.push({
          index: i,
          oldColor,
          changeType: 'removed',
        });
      } else if (oldColor && newColor) {
        if (oldColor === newColor) {
          changeList.push({
            index: i,
            oldColor,
            newColor,
            deltaE: 0,
            changeType: 'unchanged',
          });
        } else {
          const deltaE = colorEngine.calculateDeltaE(oldColor, newColor);
          changeList.push({
            index: i,
            oldColor,
            newColor,
            deltaE: deltaE ?? undefined,
            changeType: 'modified',
          });
        }
      }
    }

    return changeList;
  }, [comparisonVersion.colors, currentPalette.colors]);

  const summary = useMemo(() => {
    const added = changes.filter((c) => c.changeType === 'added').length;
    const removed = changes.filter((c) => c.changeType === 'removed').length;
    const modified = changes.filter((c) => c.changeType === 'modified').length;
    const unchanged = changes.filter((c) => c.changeType === 'unchanged').length;

    const modifiedChanges = changes.filter((c) => c.changeType === 'modified' && c.deltaE !== undefined);
    const avgDeltaE =
      modifiedChanges.length > 0
        ? modifiedChanges.reduce((sum, c) => sum + (c.deltaE ?? 0), 0) / modifiedChanges.length
        : 0;
    const maxDeltaE = modifiedChanges.length > 0 ? Math.max(...modifiedChanges.map((c) => c.deltaE ?? 0)) : 0;

    return { added, removed, modified, unchanged, avgDeltaE, maxDeltaE };
  }, [changes]);

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getChangeIcon = (changeType: ColorChange['changeType']) => {
    switch (changeType) {
      case 'added':
        return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'removed':
        return <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
      case 'modified':
        return <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
      case 'unchanged':
        return <Info className="w-4 h-4 text-gray-400 dark:text-gray-500" />;
    }
  };

  const getChangeBadge = (changeType: ColorChange['changeType']) => {
    const badges = {
      added: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
      removed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
      modified: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
      unchanged: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    };

    const labels = {
      added: 'Added',
      removed: 'Removed',
      modified: 'Modified',
      unchanged: 'Unchanged',
    };

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badges[changeType]}`}>
        {labels[changeType]}
      </span>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Palette Version Comparison</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Comparing v{comparisonVersion.version} → v{currentPalette.version}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close comparison modal"
          >
            <XCircle className="w-4 h-4" /> Close
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Summary Stats */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">Change Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wide">Added</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{summary.added}</p>
              </div>
              <div>
                <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wide">Removed</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.removed}</p>
              </div>
              <div>
                <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wide">Modified</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{summary.modified}</p>
              </div>
              <div>
                <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wide">Unchanged</p>
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{summary.unchanged}</p>
              </div>
            </div>
            {summary.modified > 0 && (
              <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wide">Avg ΔE2000</p>
                    <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                      {summary.avgDeltaE.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wide">Max ΔE2000</p>
                    <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                      {summary.maxDeltaE.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Version Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Version {comparisonVersion.version} (Old)
              </h3>
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <p>
                  <span className="font-medium">Name:</span> {comparisonVersion.name}
                </p>
                <p>
                  <span className="font-medium">Colors:</span> {comparisonVersion.colors.length}
                </p>
                <p>
                  <span className="font-medium">Date:</span> {formatTimestamp(comparisonVersion.timestamp)}
                </p>
                {comparisonVersion.author && (
                  <p>
                    <span className="font-medium">Author:</span> {comparisonVersion.author}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Version {currentPalette.version} (Current)
              </h3>
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <p>
                  <span className="font-medium">Name:</span> {currentPalette.name}
                </p>
                <p>
                  <span className="font-medium">Colors:</span> {currentPalette.colors.length}
                </p>
                <p>
                  <span className="font-medium">Date:</span> {formatTimestamp(currentPalette.modified)}
                </p>
                {currentPalette.author && (
                  <p>
                    <span className="font-medium">Author:</span> {currentPalette.author}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Color Changes */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Color-by-Color Changes</h3>
            <div className="space-y-3">
              {changes.map((change, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getChangeIcon(change.changeType)}
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Position {change.index + 1}
                      </span>
                      {getChangeBadge(change.changeType)}
                    </div>
                    {change.changeType === 'modified' && change.deltaE !== undefined && (
                      <div className="text-xs font-mono text-gray-600 dark:text-gray-400">
                        ΔE2000: <span className="font-semibold">{change.deltaE.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    {change.oldColor && (
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Old Color</p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-16 h-16 rounded border-2 border-gray-300 dark:border-gray-600"
                            style={{ backgroundColor: change.oldColor }}
                          />
                          <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                            {change.oldColor}
                          </span>
                        </div>
                      </div>
                    )}

                    {change.changeType === 'modified' && (
                      <div className="flex-shrink-0">
                        <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}

                    {change.newColor && (
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">New Color</p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-16 h-16 rounded border-2 border-gray-300 dark:border-gray-600"
                            style={{ backgroundColor: change.newColor }}
                          />
                          <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                            {change.newColor}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {change.changeType === 'modified' && change.deltaE !== undefined && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {change.deltaE < 1 && (
                          <span className="text-green-600 dark:text-green-400">
                            ✓ Imperceptible difference (ΔE &lt; 1)
                          </span>
                        )}
                        {change.deltaE >= 1 && change.deltaE < 2.3 && (
                          <span className="text-blue-600 dark:text-blue-400">
                            Minor difference, noticeable by trained eyes (1 ≤ ΔE &lt; 2.3)
                          </span>
                        )}
                        {change.deltaE >= 2.3 && change.deltaE < 5 && (
                          <span className="text-yellow-600 dark:text-yellow-400">
                            Noticeable difference (2.3 ≤ ΔE &lt; 5)
                          </span>
                        )}
                        {change.deltaE >= 5 && (
                          <span className="text-red-600 dark:text-red-400">
                            Significant difference (ΔE ≥ 5)
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
